import {
  evaluateChannelMarkupRound,
  getChannelMarkupConditionConfigByKey,
  hasMeaningfulMarkupValue,
} from '@luxuryescapes/contract-svc-promo/channelMarkup'
import type {
  ChannelMarkupCandidate,
  ChannelMarkupConditionEvaluation,
  ChannelMarkupEvaluationContext,
  ChannelMarkupEvaluationState,
  ChannelMarkupMarkup,
  ChannelMarkupMatch,
  ChannelMarkupOfferInput,
  ChannelMarkupRoundResult,
  ClientInitConfig,
} from '@luxuryescapes/contract-svc-promo/channelMarkup'

import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import {
  DEFAULT_CHANNEL_MARKUP_VALUE,
  getChannelMarkupDetails,
  getMemberPriceChannelMarkupDetails,
} from './channelMarkup'
import type { ChannelMarkupDetails } from './channelMarkup'

/**
 * Channel markup v2, evaluated client-side against the svc-promo client-init payload in two
 * rounds so the user decision is computed once and reused across offers:
 *  1. Session round (`runChannelMarkupV2SessionRound`) — user + site context; item
 *     conditions stay `pending` and the surviving candidates carry forward.
 *  2. Offer round (`runChannelMarkupV2OfferRound`) — that state + one offer → matched / no-match.
 *
 * Only ONE channel markup applies per session: the session round keeps the first channel
 * markup whose session/user conditions are all satisfied (a campaign with an unresolvable
 * session condition, e.g. utm/audience, is skipped, not left to lock the page).
 * See docs/core/promo/channel-markup-v2.md.
 */

/** The channel markup config as it lives on the client-init payload. */
export type ChannelMarkupV2Config = ClientInitConfig['channelMarkupConfig']

/** User + site context supplied to the session round (everything except the offer itself). */
export type ChannelMarkupV2SessionContext = {
  region: string
  currency: string
  subscriberTier?: string | null
  roles?: Array<string>
}

/** The minimal offer shape needed to run the offer round. */
export type ChannelMarkupV2OfferContext = {
  offerId: string
  loyaltyOfferType: string
  /** Non-member inclusive price before channel markup; read by a `packagePrice` condition. */
  total: number
  /** LuxPlus+ member total, when the offer has one. */
  luxPlusPrice?: number | null
}

/**
 * A session/user condition (region, currency, subscriberTier, roles, utm, audience) — as
 * opposed to an item condition (offerId, loyaltyOfferType, luxPlusPrice, packagePrice) that only resolves
 * at the offer round. Unknown/uncontracted types are treated as item so they don't gate the
 * session (they stay `pending` and are re-checked per offer).
 */
function isSessionCondition(evaluation: ChannelMarkupConditionEvaluation): boolean {
  return getChannelMarkupConditionConfigByKey(evaluation.condition_type)?.category === 'user'
}

/**
 * Whether a candidate can still claim the session. A session/user condition must be satisfied
 * NOW (`match`): a `no-match` fails, and a `pending` one never resolves because the client
 * session context supplies no utm or audience data — so such a candidate must not lock the
 * session (it would be picked first in config order and starve a later campaign that would
 * match). Item conditions are ignored here; they resolve per offer at the offer round.
 */
function isCandidateSessionViable(candidate: ChannelMarkupCandidate): boolean {
  return candidate.evaluations.every((evaluation) =>
    isSessionCondition(evaluation) ? evaluation.status === 'match' : true,
  )
}

/** Session-round result: state to feed the offer round + the one channel markup it resolved to. */
export type ChannelMarkupV2SessionRoundResult = {
  /** Narrowed to the winning channel markup's candidates; feeds `runChannelMarkupV2OfferRound`. */
  sessionState: ChannelMarkupEvaluationState
  /** The one channel markup the session is limited to, or `undefined` when none is viable. */
  channelMarkupId?: string
}

/**
 * Narrow a state to the first viable channel markup, keeping ALL of its markups (one campaign
 * can have several — the limit is one channel markup, not one markup; the offer round picks
 * the markup). No viable candidate → state unchanged, no channel markup.
 */
function narrowToFirstChannelMarkup(sessionState: ChannelMarkupEvaluationState): ChannelMarkupV2SessionRoundResult {
  const firstViable = sessionState.candidates.find(isCandidateSessionViable)
  if (!firstViable) {
    return { sessionState }
  }
  const candidates = sessionState.candidates.filter(
    (candidate) => candidate.channelMarkupId === firstViable.channelMarkupId,
  )
  // Reuse the original reference when nothing was removed.
  const narrowed = candidates.length === sessionState.candidates.length ? sessionState : { ...sessionState, candidates }
  return { sessionState: narrowed, channelMarkupId: firstViable.channelMarkupId }
}

/**
 * Run the session round: evaluate user + site conditions, then narrow to the first viable
 * channel markup. Item conditions stay unresolved, so the carried `sessionState` is resolved
 * per offer by `runChannelMarkupV2OfferRound`.
 */
export function runChannelMarkupV2SessionRound(
  channelMarkupConfig: ChannelMarkupV2Config,
  sessionContext: ChannelMarkupV2SessionContext,
): ChannelMarkupV2SessionRoundResult {
  const context: ChannelMarkupEvaluationContext = {
    region: sessionContext.region,
    currency: sessionContext.currency,
    subscriberTier: sessionContext.subscriberTier ?? null,
    roles: sessionContext.roles ?? [],
  }
  return narrowToFirstChannelMarkup(evaluateChannelMarkupRound({ channelMarkupConfig, context }).state)
}

/**
 * Run the offer round: feed the surviving session candidates plus a single offer and
 * resolve the remaining item conditions.
 */
export function runChannelMarkupV2OfferRound(
  sessionState: ChannelMarkupEvaluationState,
  offer: ChannelMarkupV2OfferContext,
): ChannelMarkupRoundResult {
  const offerInput: ChannelMarkupOfferInput = {
    version: 'cmOfferV1',
    offerId: offer.offerId,
    loyaltyOfferType: offer.loyaltyOfferType,
    total: offer.total,
    luxPlusPrice: offer.luxPlusPrice ?? null,
  }
  return evaluateChannelMarkupRound({ state: sessionState, context: { offer: offerInput } })
}

/** The first matched markup for a round result, or `undefined` when nothing matched. */
export function getChannelMarkupV2Match(result: ChannelMarkupRoundResult): ChannelMarkupMatch | undefined {
  // The session round keeps one channel markup, so several matches can only be several
  // markups of that campaign; the first in config order wins.
  return result.status === 'matched' ? result.matches[0] : undefined
}

/** Whether a matched markup carries a value worth displaying/charging. */
export function isChannelMarkupV2Applied(match: ChannelMarkupMatch | undefined): boolean {
  return !!match && hasMeaningfulMarkupValue(match.markup)
}

// Map-time adapter: express a v2 markup as the v1 `ChannelMarkupDetails` shape so the mapper
// apply path (`applyChannelMarkupAndRoundUpToTen`) is reused unchanged.

const NO_CHANNEL_MARKUP_V2: ChannelMarkupDetails = { markup: DEFAULT_CHANNEL_MARKUP_VALUE, isApplied: false }

// Map-time-supported offer types (accommodation entries of `LOYALTY_PRODUCT_TYPE_MAP`); other
// offer types resolve to no markup.
const LOYALTY_OFFER_TYPE_BY_OFFER_TYPE: Partial<Record<App.OfferType, App.LoyaltyProductType>> = {
  [OFFER_TYPE_HOTEL]: 'hotel_flash',
  [OFFER_TYPE_BED_BANK]: 'hotel_lpp_eps',
  [OFFER_TYPE_ALWAYS_ON]: 'hotel_lpc',
}

function getLoyaltyOfferTypeForOfferType(offerType: App.OfferType | undefined): App.LoyaltyProductType | undefined {
  return offerType ? LOYALTY_OFFER_TYPE_BY_OFFER_TYPE[offerType] : undefined
}

/**
 * Convert a matched v2 markup into the `ChannelMarkupDetails` the map-time apply path consumes:
 * `percentage` to a multiplier, `fixed` to a flat `fixedAmount`. Member pricing uses
 * `luxPlusMarkupValue`.
 */
export function channelMarkupV2MarkupToDetails(
  markup: ChannelMarkupMarkup,
  options?: { member?: boolean },
): ChannelMarkupDetails {
  const value = (options?.member ? markup.luxPlusMarkupValue : markup.markupValue) ?? null
  if (value == null || value === 0) {
    return NO_CHANNEL_MARKUP_V2
  }
  switch (markup.markupType) {
    case 'fixed': {
      // A flat amount is money, so it only means anything in a stated currency. `evaluateMarkupScope`
      // skips the currency check when the markup leaves it blank, which would add a bare `50` in
      // whatever the session is browsing in. svc-promo rejects such a markup at save time; this
      // guards the rows it cannot reach (pre-check data, scripts, direct edits). "Blank" has to
      // mean what the contract's `normalizeFilterValue` means by it — null, or a string that is
      // empty once trimmed — or a whitespace currency would skip the scope check and pass here.
      if (markup.currency == null || markup.currency.trim().length === 0) {
        return NO_CHANNEL_MARKUP_V2
      }
      // A negative amount is a flat markdown (channel discount). svc-order carries the surcharge
      // signed on every vertical — bedbank and Flash always did, LPC since svc-order #6767 — so the
      // amount is passed through as given and `applyChannelMarkupAndRoundUpToTen` subtracts it.
      return { markup: DEFAULT_CHANNEL_MARKUP_VALUE, isApplied: true, fixedAmount: value }
    }
    case 'percentage': {
      // A negative percentage is a markdown (channel discount); a positive one is a markup. Reject
      // <= -100%, which would zero out or invert the price. Both signs map to a positive multiplier
      // `1 + value / 100`, and `applyChannelMarkupAndRoundUpToTen` rounds the result up to the nearest ten.
      if (value <= -100) {
        return NO_CHANNEL_MARKUP_V2
      }
      return { markup: 1 + value / 100, isApplied: true }
    }
  }
}

/**
 * Resolve v2 `ChannelMarkupDetails` for a single offer against the session state: guard
 * the global block list, run the offer round, and convert the matched markup.
 */
export function getChannelMarkupV2Details(
  sessionState: ChannelMarkupEvaluationState,
  offer: ChannelMarkupV2OfferContext,
  blockList: Array<string>,
  options?: { member?: boolean },
): ChannelMarkupDetails {
  if (blockList.includes(offer.offerId)) {
    return NO_CHANNEL_MARKUP_V2
  }
  const match = getChannelMarkupV2Match(runChannelMarkupV2OfferRound(sessionState, offer))
  if (!isChannelMarkupV2Applied(match) || !match) {
    return NO_CHANNEL_MARKUP_V2
  }
  return channelMarkupV2MarkupToDetails(match.markup, options)
}

/**
 * A mode-tagged channel-markup source threaded through the mappers in place of the raw
 * `App.ChannelMarkup`, so the map-time apply path is agnostic to v1 vs v2. `channelMarkupId`
 * is the session's single resolved channel markup — it identifies the marked price for the
 * SSR offer cache key (see {@link getChannelMarkupV2CacheKeyPart}).
 */
export type ChannelMarkupSource =
  | { mode: 'v1', channelMarkup?: App.ChannelMarkup }
  | { mode: 'v2', sessionState?: ChannelMarkupEvaluationState, blockList: Array<string>, channelMarkupId?: string }

/**
 * The v2 fragment of the SSR offer cache key. Under v2 the marked price is decided by the
 * session's single channel markup, so its `channelMarkupId` is the discriminator — without it
 * a member and a logged-out visitor in one region would collide on one key and get each
 * other's (un)marked price. Eligibility is derived from `offerType` via the same
 * {@link getLoyaltyOfferTypeForOfferType} map that drives the actual marking, so the key can
 * never disagree with what {@link resolveChannelMarkupDetails} marks — e.g. bedbank is marked
 * under v2 just as it is under v1, so it must
 * discriminate here too. Returns `{}` under v1 (the caller keys the v1 markup itself), for an
 * offer type that is never marked at map time, or when no channel markup resolved (the
 * un-marked bucket) — so prod (v1) keys stay byte-identical.
 */
export function getChannelMarkupV2CacheKeyPart(
  source: ChannelMarkupSource | undefined,
  offerType: App.OfferType | null | undefined,
): { channelMarkupV2?: string } {
  if (!source || source.mode !== 'v2' || !source.channelMarkupId || !getLoyaltyOfferTypeForOfferType(offerType ?? undefined)) {
    return {}
  }
  return { channelMarkupV2: source.channelMarkupId }
}

const PRICE_BAND_CONDITION_TYPES = new Set<string>(['packagePrice', 'luxPlusPrice'])

/**
 * Whether the session's channel markup has a v2 price-band condition (`packagePrice` /
 * `luxPlusPrice`) left to resolve per offer — the only kind that reads an offer's prices. Lets a
 * caller skip fetching prices it would otherwise need only for such a band.
 */
export function hasChannelMarkupV2PriceBand(source: ChannelMarkupSource | undefined): boolean {
  return source?.mode === 'v2' && !!source.sessionState?.candidates.some(candidate =>
    candidate.evaluations.some(evaluation => PRICE_BAND_CONDITION_TYPES.has(evaluation.condition_type)),
  )
}

/**
 * The offer's prices for the offer round. Price-band conditions compare them against a
 * threshold or range: `packagePrice` reads `total`, `luxPlusPrice` reads `luxPlusPrice`. Both
 * must be the inclusive price BEFORE channel markup (the contract's `cmOfferV1` definition) —
 * evaluating a marked price could push it across a band edge, so display and checkout would
 * disagree on whether the markup applies. A missing price reads as absent (no match).
 */
export type ChannelMarkupV2OfferPricing = {
  total?: number
  luxPlusPrice?: number | null
}

/**
 * Stand-in for an unknown `total`. The contract types `total` as a number, and its price check
 * treats a non-finite value as "no price", which never matches whatever the apply type. `0` would
 * instead read as a real $0 price and satisfy every `LOWER-THAN` band.
 */
const UNKNOWN_OFFER_TOTAL = Number.NaN

/**
 * Build the offer-round input. A missing or zero price is unknown rather than free, mirroring how
 * callers already drop a zero member price (`luxPlusPrice || undefined`).
 */
function toChannelMarkupV2OfferContext(
  offerId: string,
  loyaltyOfferType: string,
  pricing: ChannelMarkupV2OfferPricing | undefined,
): ChannelMarkupV2OfferContext {
  return {
    offerId,
    loyaltyOfferType,
    total: pricing?.total || UNKNOWN_OFFER_TOTAL,
    luxPlusPrice: pricing?.luxPlusPrice || null,
  }
}

/**
 * Resolve `ChannelMarkupDetails` for an offer from either markup version. v1 delegates to
 * the existing helpers; v2 derives the loyalty offer type from the app offer type and
 * runs the offer round. Returns no markup when the mode has no data or the offer type is
 * not a map-time-supported vertical.
 */
export function resolveChannelMarkupDetails(
  offerId: string,
  offerType: App.OfferType | undefined,
  source: ChannelMarkupSource | undefined,
  pricing?: ChannelMarkupV2OfferPricing,
): ChannelMarkupDetails {
  if (!source) {
    return NO_CHANNEL_MARKUP_V2
  }
  if (source.mode === 'v1') {
    return getChannelMarkupDetails(offerId, offerType, source.channelMarkup)
  }
  const loyaltyOfferType = getLoyaltyOfferTypeForOfferType(offerType)
  if (!source.sessionState || !loyaltyOfferType) {
    return NO_CHANNEL_MARKUP_V2
  }
  const offer = toChannelMarkupV2OfferContext(offerId, loyaltyOfferType, pricing)
  return getChannelMarkupV2Details(source.sessionState, offer, source.blockList)
}

/** Member (LuxPlus+) equivalent of {@link resolveChannelMarkupDetails}. */
export function resolveMemberChannelMarkupDetails(
  offerId: string,
  offerType: App.OfferType | undefined,
  source: ChannelMarkupSource | undefined,
  pricing?: ChannelMarkupV2OfferPricing,
): ChannelMarkupDetails {
  if (!source) {
    return NO_CHANNEL_MARKUP_V2
  }
  if (source.mode === 'v1') {
    return getMemberPriceChannelMarkupDetails(offerId, offerType, source.channelMarkup)
  }
  const loyaltyOfferType = getLoyaltyOfferTypeForOfferType(offerType)
  if (!source.sessionState || !loyaltyOfferType) {
    return NO_CHANNEL_MARKUP_V2
  }
  const offer = toChannelMarkupV2OfferContext(offerId, loyaltyOfferType, pricing)
  return getChannelMarkupV2Details(source.sessionState, offer, source.blockList, { member: true })
}

/**
 * Channel-markup identity resolved for an order item so the backend re-applies the markup it
 * charged. Under `v1` one flat multiplier per campaign, so `channelMarkupId` alone
 * (`channel_markup_id`) is sufficient. Under `v2` a `channelMarkupId` identifies a campaign that can
 * carry several `markups[]`, so the matched `markupId` disambiguates which one applied; order
 * creation sends it as svc-order's `channel_markup_discount_id` (which carries this `markupId`), so
 * a multi-markup campaign is no longer ambiguous server-side. See docs/core/promo/channel-markup-v2.md.
 */
export type ChannelMarkupOrderIdentity =
  | { version: 'v1', channelMarkupId: string }
  | { version: 'v2', channelMarkupId: string, markupId: string }

/**
 * Resolve the order-item channel-markup identity, or `undefined` when no markup applies
 * (ineligible / blacklisted / block-listed). Mirrors {@link resolveChannelMarkupDetails}
 * (or {@link resolveMemberChannelMarkupDetails} when `options.member` is set) so the persisted
 * identity matches the price actually charged: a markup with only a member value must not be
 * persisted for a customer paying the public price, and vice versa — otherwise svc-order would
 * re-apply a markup the customer never saw.
 */
export function resolveChannelMarkupOrderIdentity(
  offerId: string,
  offerType: App.OfferType | undefined,
  source: ChannelMarkupSource | undefined,
  pricing?: ChannelMarkupV2OfferPricing,
  options?: { member?: boolean },
): ChannelMarkupOrderIdentity | undefined {
  if (!source) {
    return undefined
  }
  if (source.mode === 'v1') {
    if (!source.channelMarkup || source.channelMarkup.channelMarkupBlacklist.includes(offerId)) {
      return undefined
    }
    const v1Details = options?.member ?
        getMemberPriceChannelMarkupDetails(offerId, offerType, source.channelMarkup) :
        getChannelMarkupDetails(offerId, offerType, source.channelMarkup)
    if (!v1Details.isApplied) {
      return undefined
    }
    return { version: 'v1', channelMarkupId: source.channelMarkup.channelMarkupId }
  }
  const loyaltyOfferType = getLoyaltyOfferTypeForOfferType(offerType)
  if (!source.sessionState || !loyaltyOfferType || source.blockList.includes(offerId)) {
    return undefined
  }
  const offer = toChannelMarkupV2OfferContext(offerId, loyaltyOfferType, pricing)
  const match = getChannelMarkupV2Match(runChannelMarkupV2OfferRound(source.sessionState, offer))
  // Persist only what the map-time path actually applied for this price role, by asking the
  // same converter the mappers use — so a markup it skips is never announced to svc-order.
  // A match without a `markupId` cannot be re-applied server-side, so it counts as not-applied.
  if (!match || !channelMarkupV2MarkupToDetails(match.markup, options).isApplied || !match.markup.markupId) {
    return undefined
  }
  return { version: 'v2', channelMarkupId: match.channelMarkupId, markupId: match.markup.markupId }
}
